import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { CreditCard, Plus, Edit, Trash2, Check, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FEATURE_CATEGORIES = {
  basic: {
    title: 'Basis Features',
    features: [
      { key: 'basic_vehicle_database', label: 'Basis Fahrzeug Datenbank', description: 'Grundlegende Fahrzeugdaten' },
      { key: 'pdf_reports', label: 'PDF Reports', description: 'Diagnose-Reports als PDF' },
      { key: 'email_support', label: 'Email Support', description: 'Standard Email-Support' },
      { key: 'detailed_test_instructions', label: 'Prüfanweisungen', description: 'Mit Lokalisierung der Bauteile' }
    ]
  },
  advanced: {
    title: 'Erweiterte Features',
    features: [
      { key: 'extended_vehicle_database', label: 'Erweiterte Fahrzeugdatenbank', description: 'Umfangreiche Fahrzeugdaten' },
      { key: 'dual_ai_system', label: 'Duales KI System', description: 'Gemini 2.5 Pro + GPT-4o Validierung' },
      { key: 'custom_knowledge_base', label: 'Eigene Knowledge Base', description: 'Angepasste Wissensdatenbank' },
      { key: 'live_web_research', label: 'Live Web Recherche', description: 'Echtzeit-Websuche (Tavily)' },
      { key: 'priority_support', label: 'Prio Support', description: 'Bevorzugter Support' },
      { key: 'custom_branding', label: 'Custom Branding', description: 'Eigenes Logo in PDFs' },
      { key: 'vin_decoder', label: 'VIN Decoder', description: 'Fahrgestellnummer dekodieren' }
    ]
  },
  diagnosis: {
    title: 'Diagnose Features',
    features: [
      { key: 'actual_value_assessment', label: 'Istwert Prüfung & Beurteilung', description: 'System beurteilt eingegebene Messwerte' },
      { key: 'repair_instructions', label: 'Reparatur Anleitung', description: 'Detaillierte Reparaturschritte mit Werkzeugen' },
      { key: 'repair_manuals_access', label: 'Reparaturanleitungen', description: 'Zugriff auf Reparaturanleitungen-Kachel im Fahrzeug-PopUp' },
      { key: 'file_uploads', label: 'Datei-Uploads (Bilder/Videos/Audio)', description: 'User können Bilder, Videos und Audio-Dateien hochladen' }
    ]
  },
  obd: {
    title: 'Live OBD Features',
    features: [
      { key: 'obd_live_diagnosis', label: 'Live OBD Diagnose', description: 'Echtzeit-Fahrzeugdiagnose über OBD-Dongle' },
      { key: 'obd_remote_diagnostics', label: 'Remote Diagnose', description: 'Ferndiagnose über OBD' },
      { key: 'error_code_alerts', label: 'Fehlercode-Benachrichtigungen', description: 'Push-Benachrichtigungen bei neuen Fehlercodes' },
      { key: 'predictive_maintenance', label: 'Predictive Maintenance', description: 'KI-basierte Wartungsvorhersagen' }
    ]
  },
  tuning: {
    title: 'Tuning Features',
    features: [
      { key: 'tuning_configurator', label: 'Tuning-Konfigurator', description: 'Einbettbarer Tuning-Konfigurator für Ihre Website' },
      { key: 'tuning_data', label: 'Tuning-Daten', description: 'Zugriff auf Tuning-Daten im Fahrzeug-PopUp' }
    ]
  }
};

export const AdminPlansNew = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPlan, setEditingPlan] = useState(null);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const cacheBuster = Date.now();
      const response = await axios.get(`${API}/subscriptions/plans?_=${cacheBuster}`, {
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      const plansData = response.data || [];
      
      // Convert backend format to frontend format
      const convertedPlans = plansData.map(plan => ({
        ...plan,
        interval: plan.billing_interval === 'lifetime' ? 'einmalig' : 'monatlich'
      }));
      
      setPlans(convertedPlans);
    } catch (error) {
      toast.error('Fehler beim Laden der Pläne');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePlan = () => {
    setEditingPlan({
      id: `plan_${Date.now()}`,
      name: '',
      price: 0,
      interval: 'monatlich',
      stripe_price_id: '',
      limits: {
        sessions_per_month: 10,
        storage_gb: 5,
        max_uploads_per_session: 10
      },
      features: {
        // Basic
        basic_vehicle_database: true,
        pdf_reports: true,
        email_support: true,
        detailed_test_instructions: true,
        // Advanced
        extended_vehicle_database: false,
        dual_ai_system: false,
        custom_knowledge_base: false,
        live_web_research: false,
        priority_support: false,
        custom_branding: false,
        vin_decoder: false,
        // Diagnosis
        actual_value_assessment: false,
        repair_instructions: false,
        repair_manuals_access: false,
        file_uploads: false,
        // Live OBD
        obd_live_diagnosis: false,
        obd_remote_diagnostics: false,
        error_code_alerts: false,
        predictive_maintenance: false,
        // Tuning
        tuning_configurator: false,
        tuning_data: false
      }
    });
    setIsCreating(true);
  };

  const handleSavePlan = async () => {
    try {
      // Validate
      if (!editingPlan.name || editingPlan.price < 0) {
        toast.error('Bitte füllen Sie alle Pflichtfelder aus');
        return;
      }

      // Convert interval to backend format
      const planData = {
        ...editingPlan,
        billing_interval: editingPlan.interval === 'einmalig' ? 'lifetime' : 'monthly'
      };
      
      // Remove old interval field
      delete planData.interval;

      if (isCreating) {
        const response = await axios.post(`${API}/admin/plans`, planData);
        
        // Show success message with Stripe sync status
        if (response.data.stripe_synced) {
          toast.success(`✅ Plan erstellt & automatisch mit Stripe synchronisiert!\nStripe Price ID: ${response.data.stripe_price_id}`, {
            duration: 5000
          });
        } else {
          toast.success('Plan erfolgreich erstellt');
        }
      } else {
        const response = await axios.put(`${API}/admin/plans/${editingPlan.id}`, planData);
        
        if (response.data.stripe_synced) {
          toast.success(`✅ Plan aktualisiert & mit Stripe synchronisiert!`, {
            duration: 4000
          });
        } else {
          toast.success('Plan erfolgreich aktualisiert');
        }
      }
      
      await fetchPlans();
      setEditingPlan(null);
      setIsCreating(false);
    } catch (error) {
      toast.error('Fehler beim Speichern des Plans');
      console.error(error);
    }
  };

  const handleDeletePlan = async (planId) => {
    if (!window.confirm('Plan wirklich löschen?')) return;
    
    try {
      await axios.delete(`${API}/admin/plans/${planId}`);
      toast.success('Plan gelöscht');
      await fetchPlans();
    } catch (error) {
      toast.error('Fehler beim Löschen');
      console.error(error);
    }
  };

  const toggleFeature = (featureKey) => {
    setEditingPlan(prev => ({
      ...prev,
      features: {
        ...prev.features,
        [featureKey]: !prev.features[featureKey]
      }
    }));
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Lade...</div>;
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold" style={{ color: '#E6EDF3' }}>
          Subscription Pläne verwalten
        </h1>
        <Button
          onClick={handleCreatePlan}
          style={{ backgroundColor: '#1EA0E6', color: '#ffffff' }}
        >
          <Plus className="w-4 h-4 mr-2" />
          Neuer Plan
        </Button>
      </div>

      {/* Editing/Creating Form */}
      {editingPlan && (
        <Card className="mb-6" style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
          <CardHeader>
            <CardTitle style={{ color: '#E6EDF3' }}>
              {isCreating ? 'Neuen Plan erstellen' : 'Plan bearbeiten'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block mb-2 text-sm font-semibold" style={{ color: '#E6EDF3' }}>
                    Plan Name *
                  </label>
                  <Input
                    value={editingPlan.name}
                    onChange={(e) => setEditingPlan({ ...editingPlan, name: e.target.value })}
                    placeholder="z.B. Professional"
                    style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                  />
                </div>

                <div>
                  <label className="block mb-2 text-sm font-semibold" style={{ color: '#E6EDF3' }}>
                    Preis (€) *
                  </label>
                  <Input
                    type="number"
                    value={editingPlan.price}
                    onChange={(e) => setEditingPlan({ ...editingPlan, price: parseFloat(e.target.value) || 0 })}
                    placeholder="29.99"
                    style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                  />
                </div>

                <div>
                  <label className="block mb-2 text-sm font-semibold" style={{ color: '#E6EDF3' }}>
                    Abrechnungsintervall *
                  </label>
                  <select
                    value={editingPlan.interval}
                    onChange={(e) => setEditingPlan({ ...editingPlan, interval: e.target.value })}
                    className="w-full p-2 rounded-lg"
                    style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3', border: '1px solid #1F2937' }}
                  >
                    <option value="monatlich">Monatlich</option>
                    <option value="einmalig">Einmalig</option>
                  </select>
                </div>

                <div>
                  <label className="block mb-2 text-sm font-semibold" style={{ color: '#E6EDF3' }}>
                    Diagnosen pro Monat
                    <span className="text-xs ml-2" style={{ color: '#94A3B8' }}>(0 = Unbegrenzt)</span>
                  </label>
                  <Input
                    type="number"
                    value={editingPlan.limits.sessions_per_month}
                    onChange={(e) => setEditingPlan({
                      ...editingPlan,
                      limits: { ...editingPlan.limits, sessions_per_month: parseInt(e.target.value) || 0 }
                    })}
                    placeholder="10"
                    style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                  />
                </div>
              </div>

              {/* Stripe Auto-Sync Info */}
              <div className="p-4 rounded-lg" style={{ backgroundColor: '#1E3A8A20', borderColor: '#3B82F6', border: '1px solid' }}>
                <div className="flex items-start gap-3">
                  <CreditCard className="w-5 h-5 mt-0.5" style={{ color: '#7CD4FD' }} />
                  <div>
                    <h4 className="font-semibold mb-1" style={{ color: '#7CD4FD' }}>
                      ✨ Automatische Stripe-Synchronisation
                    </h4>
                    <p className="text-sm mb-2" style={{ color: '#94A3B8' }}>
                      Wenn Stripe konfiguriert ist, wird automatisch ein Produkt und Preis in Ihrem Stripe-Dashboard erstellt.
                      Sie müssen keine Stripe Price ID manuell eingeben!
                    </p>
                    <p className="text-xs" style={{ color: '#6B7280' }}>
                      Optional: Geben Sie eine bestehende Stripe Price ID ein, um einen existierenden Stripe-Preis zu verwenden.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block mb-2 text-sm font-semibold" style={{ color: '#E6EDF3' }}>
                  Stripe Price ID (optional - wird automatisch erstellt)
                </label>
                <Input
                  value={editingPlan.stripe_price_id || ''}
                  onChange={(e) => setEditingPlan({ ...editingPlan, stripe_price_id: e.target.value })}
                  placeholder="Leer lassen für automatische Erstellung"
                  style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                />
                {editingPlan.stripe_price_id && (
                  <p className="text-xs mt-1" style={{ color: '#10B981' }}>
                    ✓ Verwendet existierenden Stripe-Preis
                  </p>
                )}
              </div>

              {/* Features */}
              <div className="space-y-4">
                <h3 className="text-xl font-bold mb-4" style={{ color: '#7CD4FD' }}>
                  Inbegriffene Features
                </h3>

                {Object.entries(FEATURE_CATEGORIES).map(([categoryKey, category]) => (
                  <div key={categoryKey} className="p-4 rounded-lg" style={{ backgroundColor: '#1E293B', borderColor: '#1F2937', border: '1px solid' }}>
                    <h4 className="text-lg font-semibold mb-3" style={{ color: '#E6EDF3' }}>
                      {category.title}
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {category.features.map((feature) => (
                        <div
                          key={feature.key}
                          className="flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-all hover:bg-white/5"
                          onClick={() => toggleFeature(feature.key)}
                          style={{ backgroundColor: editingPlan.features[feature.key] ? '#1EA0E6' : '#0B0F14', borderColor: '#1F2937', border: '1px solid' }}
                        >
                          <div className="flex-shrink-0 mt-1">
                            {editingPlan.features[feature.key] ? (
                              <Check className="w-5 h-5 text-white" />
                            ) : (
                              <X className="w-5 h-5 text-gray-500" />
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="font-semibold text-sm" style={{ color: editingPlan.features[feature.key] ? '#FFFFFF' : '#E6EDF3' }}>
                              {feature.label}
                            </div>
                            <div className="text-xs mt-1" style={{ color: editingPlan.features[feature.key] ? '#E6EDF3' : '#94A3B8' }}>
                              {feature.description}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Actions */}
              <div className="flex gap-3 justify-end">
                <Button
                  onClick={() => {
                    setEditingPlan(null);
                    setIsCreating(false);
                  }}
                  style={{ backgroundColor: '#1F2937', color: '#E6EDF3' }}
                >
                  Abbrechen
                </Button>
                <Button
                  onClick={handleSavePlan}
                  style={{ backgroundColor: '#1EA0E6', color: '#ffffff' }}
                >
                  <Check className="w-4 h-4 mr-2" />
                  Speichern
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Plans List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {plans.map((plan) => (
          <Card key={plan.id} style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
            <CardHeader>
              <CardTitle style={{ color: '#E6EDF3' }}>
                <div className="flex items-center justify-between">
                  <span>{plan.name}</span>
                  <CreditCard className="w-5 h-5" style={{ color: '#7CD4FD' }} />
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <div className="text-3xl font-bold" style={{ color: '#7CD4FD' }}>
                    {plan.price}€
                  </div>
                  <div className="text-sm" style={{ color: '#94A3B8' }}>
                    {plan.interval === 'einmalig' ? 'Einmalig' : 'pro Monat'}
                  </div>
                </div>

                <div className="text-sm" style={{ color: '#E6EDF3' }}>
                  <strong>Diagnosen:</strong>{' '}
                  {plan.limits?.sessions_per_month === 0 || plan.limits?.sessions_per_month === null || plan.limits?.sessions_per_month === undefined 
                    ? 'Unbegrenzt' 
                    : `${plan.limits.sessions_per_month}/Monat`}
                </div>

                <div className="text-xs space-y-1">
                  <div style={{ color: '#94A3B8' }}>Aktive Features:</div>
                  {Object.entries(plan.features || {}).filter(([_, enabled]) => enabled).slice(0, 3).map(([key]) => (
                    <div key={key} className="flex items-center gap-1" style={{ color: '#7CD4FD' }}>
                      <Check className="w-3 h-3" />
                      <span>{key.replace(/_/g, ' ')}</span>
                    </div>
                  ))}
                  {Object.values(plan.features || {}).filter(v => v).length > 3 && (
                    <div style={{ color: '#94A3B8' }}>
                      +{Object.values(plan.features || {}).filter(v => v).length - 3} weitere
                    </div>
                  )}
                </div>

                <div className="flex gap-2 pt-4">
                  <Button
                    onClick={() => {
                      setEditingPlan(plan);
                      setIsCreating(false);
                    }}
                    className="flex-1"
                    style={{ backgroundColor: '#1F2937', color: '#E6EDF3' }}
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Bearbeiten
                  </Button>
                  <Button
                    onClick={() => handleDeletePlan(plan.id)}
                    style={{ backgroundColor: '#DC2626', color: '#ffffff' }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {plans.length === 0 && !editingPlan && (
        <div className="text-center py-12" style={{ color: '#94A3B8' }}>
          Noch keine Pläne vorhanden. Erstellen Sie einen neuen Plan.
        </div>
      )}
    </div>
  );
};

export default AdminPlansNew;
